import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  Pencil,
  Trash2,
  FileText,
} from "lucide-react";

import {
  useGetAdminArticlesQuery,
  useDeleteArticleMutation,
  useGetFeedbackQuery,
  Article
} from "../../../../store/apis/helpCenter.api";

import {
  Button,
  SearchInput,
  ConfirmationModal,
  SimpleTooltip,
  Badge,
  DataTable,
  DataTableCard,
  Pagination2,
  ColumnSelector,
  StatsCards,
  TabSelector,
  StatCardSkeleton,
} from "../../../common";

import { TableColumn } from "../../../common/Table.tsx";
import ArticleModal from "../modals/ArticleModal";
import { useHasPermission } from "../../../../store/hooks/useRbac";
import { PERMISSIONS } from "../../../../utils/rbac/permissions";
import { ArticleTableSkeleton } from "../Skeleton";
import { HELP_ADMIN_STAT_CARDS } from "../utils";
import { HELP_STATIC_CATEGORIES, mapModuleToCategory, type HelpStaticKey } from "../../../../utils/helpCenter";
import { formatDDMMYYYY } from "../../../../utils/timeUtils";

// Extend type to include backend _id
type ArticleWithId = Article & { _id: string };

type CategoryTab = "All" | HelpStaticKey;

const ManageArticles: React.FC = () => {
  const canManage = useHasPermission(PERMISSIONS.HELP_CENTER_ADMIN_MANAGE || "HELP_CENTER_ADMIN_VIEW");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCat, setActiveCat] = useState<CategoryTab>("All");

  // Table page (server-paginated, Figma "All" view)
  const { data, isLoading, isFetching, isError, refetch } = useGetAdminArticlesQuery({
    page,
    limit,
    search: searchTerm || undefined,
  });

  // Full dataset for real stats + static-category counts/tabs (lightweight, UI-level)
  const { data: allData, isLoading: allLoading, isError: allError, refetch: refetchAll } = useGetAdminArticlesQuery({
    page: 1,
    limit: 1000,
  });

  // Feedback total for the 4th stat (real data — no fabricated helpful %)
  const { data: fbData, isLoading: fbLoading } = useGetFeedbackQuery({ page: 1, limit: 1 });

  const articles: ArticleWithId[] = (data as any)?.data?.data || [];
  const total = (data as any)?.data?.total || 0;
  const allArticles: ArticleWithId[] = (allData as any)?.data?.data || [];
  const allTotal = (allData as any)?.data?.total ?? allArticles.length;
  const feedbackTotal = (fbData as any)?.total ?? 0;

  const publishedCount = useMemo(
    () => allArticles.filter((a) => a.isPublished).length,
    [allArticles]
  );

  const stats = useMemo(
    () => ({
      total: allTotal,
      published: publishedCount,
      drafts: Math.max(0, allTotal - publishedCount),
      feedback: feedbackTotal,
    }),
    [allTotal, publishedCount, feedbackTotal]
  );

  const statsLoading = isLoading || allLoading || fbLoading;

  const tabs = useMemo(
    () => [
      { k: "All" as CategoryTab, label: "All", count: allTotal },
      ...HELP_STATIC_CATEGORIES.map((c) => ({
        k: c.key as CategoryTab,
        label: c.label,
        count: allArticles.filter((a) => mapModuleToCategory(a.moduleCode).key === c.key).length,
      })),
    ],
    [allArticles, allTotal]
  );

  const isCatFiltered = activeCat !== "All";

  // Static-category view filters the full dataset client-side (server has
  // no multi-code category param); "All" stays server-paginated.
  const catArticles = useMemo(() => {
    if (!isCatFiltered) return [];
    const q = searchTerm.trim().toLowerCase();
    return allArticles.filter((a) => {
      if (mapModuleToCategory(a.moduleCode).key !== activeCat) return false;
      if (!q) return true;
      return (
        a.title.toLowerCase().includes(q) ||
        (a.content || "").toLowerCase().includes(q)
      );
    });
  }, [allArticles, activeCat, isCatFiltered, searchTerm]);

  const displayRows = isCatFiltered
    ? catArticles.slice((page - 1) * limit, page * limit)
    : articles;
  const displayTotal = isCatFiltered ? catArticles.length : total;
  const tableLoading = isLoading || isFetching || (isCatFiltered && allLoading);

  const [deleteArticle, { isLoading: isDeleting }] = useDeleteArticleMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<ArticleWithId | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<string[]>(["category", "helpful", "views"]);

  const toggleVisibility = (key: string) => {
    setVisibleKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Edit
  const handleEdit = (article: ArticleWithId) => {
    setSelectedArticle(article);
    setIsModalOpen(true);
  };

  // Delete
  const handleDelete = async () => {
    if (deleteId) {
      try {
        await deleteArticle(deleteId).unwrap();
        toast.success("Article deleted successfully");
      } catch (error) {
        console.error("Failed to delete article:", error);
        toast.error("Failed to delete article");
      } finally {
        setDeleteId(null);
      }
    }
  };

  // Figma table columns: Article / Category / Helpful / Views / Status / Actions
  const columns: TableColumn<ArticleWithId>[] = useMemo(
    () => [
      {
        key: "title",
        title: "Article",
        label: "Article",
        required: true,
        render: (_, record) => {
          const cat = mapModuleToCategory(record.moduleCode);
          return (
            <div className="flex items-center gap-[11px]">
              <div className={`w-[38px] h-[38px] rounded-[11px] grid place-items-center flex-none ${cat.tileBg}`}>
                <FileText className={`w-[18px] h-[18px] ${cat.tileColor}`} />
              </div>
              <div className="min-w-0 flex-1">
                <SimpleTooltip label={record.title} side="top">
                  <span className="font-bold text-label text-[var(--color-text-primary)] block truncate">
                    {record.title}
                  </span>
                </SimpleTooltip>
                <span className="block text-body-xs text-[var(--color-text-tertiary)] truncate">
                  {cat.label} · Updated {formatDDMMYYYY(record.updatedAt) ?? "—"}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        key: "category",
        title: "Category",
        label: "Category",
        render: (_, record) => {
          const cat = mapModuleToCategory(record.moduleCode);
          return (
            <span className={`inline-flex items-center text-caption font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${cat.tileBg} ${cat.tileColor}`}>
              {cat.label}
            </span>
          );
        },
      },
      {
        key: "helpful",
        title: "Helpful",
        label: "Helpful",
        render: (_, record) => {
          if (record.helpful == null) {
            return <span className="text-body-sm font-bold text-[var(--color-text-tertiary)]">—</span>;
          }
          const colorClass =
            record.helpful >= 85
              ? "text-[var(--color-success-dark,#059669)]"
              : record.helpful >= 75
              ? "text-[var(--color-warning-dark,#b45309)]"
              : "text-[var(--color-error-dark,#dc2626)]";
          return (
            <span className={`text-body-sm font-bold ${colorClass}`}>
              {record.helpful}%
            </span>
          );
        },
      },
      {
        key: "views",
        title: "Views",
        label: "Views",
        render: (_, record) => (
          <span className="text-body-sm font-semibold text-[var(--color-text-secondary)]">
            {(record.views ?? 0).toLocaleString()}
          </span>
        ),
      },
      {
        key: "isPublished",
        title: "Status",
        label: "Status",
        required: true,
        render: (_, record) => (
          <Badge variant={record.isPublished ? "green" : "gray"} size="middle">
            {record.isPublished ? "Published" : "Draft"}
          </Badge>
        ),
      },
      {
        key: "actions",
        title: "Actions",
        label: "Actions",
        required: true,
        align: "right",
        render: (_, record) => (
          <div className="flex items-center justify-end gap-1">
            {canManage && (
              <>
                <SimpleTooltip label="Edit article" side="top">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.currentTarget.blur();
                      handleEdit(record);
                    }}
                    aria-label="Edit article"
                    className="relative w-8 h-8 grid place-items-center rounded-button text-[var(--color-text-secondary)] hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-700)] transition-colors cursor-pointer border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none after:absolute after:-inset-2 after:content-['']"
                  >
                    <Pencil className="w-[15px] h-[15px]" aria-hidden="true" />
                  </button>
                </SimpleTooltip>
                <SimpleTooltip label="Delete article" side="top">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.currentTarget.blur();
                      setDeleteId(record._id);
                    }}
                    aria-label="Delete article"
                    className="relative w-8 h-8 grid place-items-center rounded-button text-[var(--color-text-tertiary)] hover:bg-[var(--color-error-light)] hover:text-[var(--color-error-dark)] transition-colors cursor-pointer border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none after:absolute after:-inset-2 after:content-['']"
                  >
                    <Trash2 className="w-[15px] h-[15px]" aria-hidden="true" />
                  </button>
                </SimpleTooltip>
              </>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManage]
  );

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.required || visibleKeys.includes(c.key)),
    [columns, visibleKeys]
  );

  // Modal control from parent via event (logic unchanged)
  React.useEffect(() => {
    const handleOpenModal = () => {
      setSelectedArticle(null);
      setIsModalOpen(true);
    };
    window.addEventListener("open-article-modal", handleOpenModal);
    return () => window.removeEventListener("open-article-modal", handleOpenModal);
  }, []);

  if (isError || allError) {
    return (
      <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-[18px] shadow-[var(--shadow-sm)] text-center px-6 py-14">
        <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">Failed to load articles</h3>
        <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
          Something went wrong while fetching help articles. Please try again.
        </p>
        <Button
          appearance="primary"
          size="small"
          onClick={() => {
            refetch();
            refetchAll();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Figma stat cards with real data */}
      {statsLoading ? (
        <StatCardSkeleton count={4} />
      ) : (
        <div className="mb-5 sm:mb-6">
          <StatsCards stats={stats} cards={HELP_ADMIN_STAT_CARDS} variant="simple-icon" valueOnTop />
        </div>
      )}

      {/* Figma category filter tabs */}
      <div className="overflow-x-auto no-scrollbar">
        <TabSelector
          tabs={tabs}
          active={activeCat}
          onChange={(k) => {
            setActiveCat(k);
            setPage(1);
          }}
        />
      </div>

      <DataTableCard
        title="Help Center Articles"
        count={displayTotal}
        toolbar={
          <>
            <SearchInput
              value={searchTerm}
              onChange={(val) => {
                setSearchTerm(val);
                setPage(1);
              }}
              placeholder="Search articles..."
              className="w-full sm:w-[220px]"
            />
            <ColumnSelector columns={columns} visibleKeys={visibleKeys} onToggle={toggleVisibility} />
          </>
        }
        pagination={
          displayTotal > limit ? (
            <Pagination2
              total={displayTotal}
              page={page}
              pageSize={limit}
              pageSizeOptions={[5, 10, 20, 50]}
              onPageChange={setPage}
              onPageSizeChange={(l) => {
                setLimit(l);
                setPage(1);
              }}
            />
          ) : undefined
        }
      >
        {!tableLoading && displayRows.length === 0 ? (
          <div className="px-[22px] py-10 text-center border-t border-[var(--color-border-tertiary)]">
            <div className="text-body-md font-bold text-[var(--color-text-primary)]">
              No articles in this category yet
            </div>
            <div className="text-body-sm text-[var(--color-text-tertiary)] mt-1">
              Create the first one with New Article.
            </div>
          </div>
        ) : (
          <DataTable
            columns={visibleColumns}
            rows={displayRows}
            rowKey={(r) => r._id}
            loading={tableLoading}
            skeleton={<ArticleTableSkeleton />}
            emptyMessage="No articles found"
            responsive="scroll"
          />
        )}
      </DataTableCard>

      {/* Modal */}
      <ArticleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        article={selectedArticle}
      />

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Article"
        message="Are you sure you want to delete this article? This action cannot be undone."
        isLoading={isDeleting}
        type="danger"
      />
    </div>
  );
};

export default ManageArticles;
